import React, { useState } from 'react';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import ProductWizardState from '../../model/ProductWizardState';
import { useTrans, useUniqueWcagValues } from 'common-react';
import IkzeExtendedEntrepreneurDepositDialog from './IkzeExtendedEntrepreneurDepositDialog';
import { formatCurrencyWithoutCode, Radio, RadioGroup, ConsentGroup, FormControlLabel, Typography, Card, Divider, Link } from 'ui-components';
import { useTranslation } from 'react-i18next';
import { validateNip } from '../NipValidator/validateNip';
import { CustomForm } from '../formik/CustomForm/CustomForm';
import FormikTextField from '../formik/FormikTextField/FormikTextField';
import { ReactComponent as CheckIcon } from 'common-assets/assets/icons/check-circled-green.svg';

import useStyles from './IkzeExtendedEntrepreneurDepositAgreement.style';

export interface IkzeExtendedEntrepreneurDepositAgreementForm {
  isEntrepreneur: boolean,
  nip?: string,
  entrepreneurConsent?: boolean }

export interface IkzeExtendedEntrepreneurDepositAgreementProps {
  initialValues: IkzeExtendedEntrepreneurDepositAgreementForm;
  handleChange: (values: IkzeExtendedEntrepreneurDepositAgreementForm) => void;
  handleValid: (value: boolean) => void;
  className?: string;
}

const IkzeExtendedEntrepreneurDepositAgreement: React.FC<IkzeExtendedEntrepreneurDepositAgreementProps>
    = ({ handleChange, handleValid, initialValues, className }) => {
  const c = useStyles({});
  const limits = useSelector((state: ProductWizardState) => state.apiState.currentLimits);
  const { trans } = useTrans('ikze-extended-entrepreneur-deposit');
  const [t] = useTranslation('ikze-extended-entrepreneur-deposit');
  const [ikzeDialogOpen, setIkzeDialogOpen] = useState(false);
  const ikzeEntrepreneurLimit = limits.currentLimits.IKZE.entrepreneurLimit ? limits.currentLimits.IKZE.entrepreneurLimit : 0;
  const currentYear = new Date().getFullYear();
  const { getUniqueValue } = useUniqueWcagValues();
  const radioGroupId = getUniqueValue('entrepreneurRadioGroup');
  const renderAgreementText = () => {
    const onClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setIkzeDialogOpen(true);
    };
    return <Typography textColor="greys.60" className={c.agreementText}>
        {trans('agreement', '', { amount: formatCurrencyWithoutCode(ikzeEntrepreneurLimit), year: currentYear }, [
          <Link as="link" href="#" key="DialogLink" onClick={onClick}>
            {trans('agreement', '', { amount: formatCurrencyWithoutCode(ikzeEntrepreneurLimit), year: currentYear })}
          </Link>
        ])}
      </Typography>
  };



  return (
    <Card className={clsx(className, c.root)}>
      <Formik<IkzeExtendedEntrepreneurDepositAgreementForm>
        initialValues={initialValues}
        validationSchema={Yup.object().shape({
          isEntrepreneur: Yup.boolean()
                              .required()
                              .oneOf([true, false]),
          nip: Yup.string().when('isEntrepreneur', {
            is: true,
            then: Yup.string()
                      .required(t('nip_required'))
                      .min(10, t('nip_invalid'))
                      .max(10, t('nip_invalid'))
                      .test('validate-nip', t('nip_invalid'), (nip: string) => validateNip(nip)),
            otherwise: Yup.string()
                          .nullable()
                          .transform(() => null) }),
          entrepreneurConsent: Yup.boolean().when('isEntrepreneur', {
            is: true,
            then: Yup.boolean()
                      .required()
                      .oneOf([true], t('entrepreneur_statement_required')),
            otherwise: Yup.boolean()
                          .nullable()
                          .transform(() => null) }) })}
        enableReinitialize={true}
        isInitialValid={true}
        onSubmit={() => {}}
      >
        {({ values, isSubmitting, isValid, setFieldValue }) => {
          handleValid(!isSubmitting && isValid);
          handleChange(values);

          return (
            <CustomForm autoComplete={'nope'}>
              <Typography variant="h3" fontWeight="bold" textColor="greys.80">
                {trans('header')}
              </Typography>
              {renderAgreementText()}
              <RadioGroup
                name="isEntrepreneur"
                value={values.isEntrepreneur ? 'true' : 'false'}
                onChange={(e) => setFieldValue('isEntrepreneur', e.target.value === 'true')}
                aria-labelledby={radioGroupId}
                className={c.radioContainer}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label={trans('radio_no')}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label={trans('radio_yes')}
                />
              </RadioGroup>

              {values.isEntrepreneur && (
                <div className={c.entrepreneurSection}>
                  <FormikTextField
                    name="nip"
                    label={t('nip_label')}
                    placeholder={t('nip_label_placeholder')}
                    className={c.nipField}
                    required={true}
                    autoComplete="off"
                    endAdornment={values.nip && validateNip(values.nip) ? <CheckIcon /> : undefined}
                    testId="nip-field"
                  />
                  <Divider />
                  <ConsentGroup
                    title={t('statement_title')}
                    titleVariant="h3"
                    titleFontWeight="bold"
                    description={t('statement_required')}
                    items={[{
                      id: 'entrepreneurConsent',
                      name: 'entrepreneurConsent',
                      label: t('entrepreneur_statement_label'),
                      description: t('entrepreneur_statement_description'),
                      checked: values.entrepreneurConsent || false,
                      onChange: (checked: boolean) => setFieldValue('entrepreneurConsent', checked),
                      required: true }]}
                  />
                </div>
              )}
            </CustomForm>
          );
        }}
      </Formik>
      <IkzeExtendedEntrepreneurDepositDialog open={ikzeDialogOpen} onClose={() => setIkzeDialogOpen(false)}/>
    </Card>
  );
};

export default IkzeExtendedEntrepreneurDepositAgreement;
